import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Check, ArrowLeft, Loader2 } from "lucide-react";
import "./DogPersonalityEditPage.css";
import progressIcon from "../../assets/progressIcon4.png";
import option1Icon from "../../assets/girlAndDog1.png";
import option2Icon from "../../assets/girlAndDog2.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";
import appIcon from "../../assets/appIcon.svg";
import apiClient from "../../api/apiClient";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";

const DogPersonalityEditPage = () => {
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.user);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Mobile Step State (1 for Left Panel, 2 for Right Panel)
  const location = useLocation();
  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);

  const [formData, setFormData] = useState({
    dogName: "",
    dogActivity: "",
    dogPersonality: {},
    rawAnswers: {},
  });

  const [dynamicPrompts, setDynamicPrompts] = useState([]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          const userData = res.data.data;
          setUser(userData);

          if (userData.dogs && userData.dogs.length > 0) {
            const dog = userData.dogs[0];
            const fetchedDogName = dog.dog_name || "";

            // Dynamically generate default prompts with the fetched name
            const defaultPromptsForDog = [
              `One of ${fetchedDogName}'s quirks is`,
              "My dog is so sweet when",
              "The inspo for my dog's name is",
              "My dog's coolest party trick is",
              "The weirdest thing my dog has eaten is",
            ];

            const personalityMap = {};
            const rawMap = {};
            let updatedPrompts = [...defaultPromptsForDog];

            if (Array.isArray(dog.personality)) {
              dog.personality.forEach((fullString) => {
                const foundIdx = defaultPromptsForDog.findIndex((p) =>
                  fullString.startsWith(p),
                );

                if (foundIdx !== -1) {
                  const promptText = defaultPromptsForDog[foundIdx];
                  const answerOnly = fullString.replace(promptText, "").trim();
                  personalityMap[foundIdx] = fullString;
                  rawMap[foundIdx] = answerOnly;
                } else {
                  const splitIndex = fullString.lastIndexOf(" ");
                  const customQ = fullString.substring(0, splitIndex).trim();
                  const customA = fullString.substring(splitIndex).trim();

                  const newIdx = updatedPrompts.length;
                  updatedPrompts.push(customQ);

                  personalityMap[newIdx] = fullString;
                  rawMap[newIdx] = customA;
                }
              });
            }

            setDynamicPrompts(updatedPrompts);
            setFormData((prev) => ({
              ...prev,
              dogName: fetchedDogName,
              dogActivity: dog.dog_activity?.toLowerCase() || "",
              dogPersonality: personalityMap,
              rawAnswers: rawMap,
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      } finally {
        setLoading(false);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  // Helpers
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePromptAnswerChange = (index, promptText, answerValue) => {
    const combinedValue = answerValue ? `${promptText} ${answerValue}` : "";
    setFormData((prev) => ({
      ...prev,
      dogPersonality: { ...prev.dogPersonality, [index]: combinedValue },
      rawAnswers: { ...prev.rawAnswers, [index]: answerValue },
    }));
  };

  const handleCustomChange = (question, answer) => {
    const combinedCustom = question || answer ? `${question} ${answer}` : "";

    setFormData((prev) => ({
      ...prev,
      customPromptQuestion: question,
      customPromptAnswer: answer,
      dogPersonality: {
        ...(prev.dogPersonality || {}),
        custom: combinedCustom,
      },
    }));
  };

  const answerdpromptsCount =
    Object.values(formData.rawAnswers).filter((val) => val && val.trim() !== "")
      .length + (formData.customPromptAnswer ? 1 : 0);

  const isExperienceSelected = Boolean(formData.dogActivity);
  const isAnyPromptAnswered = answerdpromptsCount > 0;

  const isNextEnabled = isExperienceSelected && isAnyPromptAnswered;

  const handleNext = async () => {
    if (!isNextEnabled || isUpdating) return;
    setIsUpdating(true);
    try {
      const dogId = user?.dogs?.[0]?.id;
      const apiPayload = {
        user_id: profile?.id,
        dog_activity:
          formData.dogActivity === "both"
            ? "Both"
            : formData.dogActivity.charAt(0).toUpperCase() +
              formData.dogActivity.slice(1),
        personality: JSON.stringify(
          Object.values(formData.dogPersonality).filter(
            (s) => s.trim().length > 10,
          ),
        ),
      };

      const response = await apiClient.put(
        `/dogs/update-dog-by-id/${dogId}`,
        apiPayload,
      );
      if (response.data?.status) navigate("/edit-team-paws-personality");
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div
      className={`edpe-container ${mobileStep === 1 ? "edpe-mobile-step-1" : "edpe-mobile-step-2"}`}
    >
      {/* LEFT PANEL: Experience Selection (Mobile Step 1) */}
      <div
        className={`edpe-panel edpe-left-panel ${mobileStep === 1 ? "edpe-mobile-active" : "edpe-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => {
            if (window.innerWidth <= 1023) {
              navigate("/edit-dog-details", { state: { step: 2 } });
            } else {
              navigate(-1);
            }
          }}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
        />

        <DesktopTopBar
          showBack={true}
          onBack={() => navigate("/edit-dog-details")}
          backColor="#000"
        />

        <div className="edpe-content-wrapper">
          {/* Header & Progress */}
          <div className="edpe-header-section" style={{ marginTop: "-4px" }}>
            <div className="edpe-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress Step"
                className="edpe-progress-icon"
                style={
                  window.innerWidth <= 1023
                    ? { height: "60px", marginLeft: "-1rem" }
                    : {}
                }
              />
            </div>
            <div className="edpe-progress-track">
              <div className="edpe-progress-fill edpe-step-1-fill"></div>
            </div>
          </div>

          <h1 className="edpe-title-text">
            WHAT EXPERIENCE IS{" "}
            {formData.dogName ? formData.dogName.toUpperCase() : "[DOG NAME]"}{" "}
            WELL-SUITED FOR?
          </h1>

          <div className="edpe-options-container">
            {/* Option 1: Exercise */}
            <div className="edpe-option-wrapper">
              <div
                className={`edpe-option-card ${
                  formData.dogActivity === "exercise" ? "selected" : ""
                }`}
                onClick={() => updateField("dogActivity", "exercise")}
              >
                <div className="edpe-exercise-img">
                  <img
                    src={option1Icon}
                    alt="Girl Playing"
                    className="edpe-img-contain"
                  />
                </div>
                <div className="edpe-card-content">
                  <h3>EXERCISE</h3>
                </div>
              </div>
              <p className="edpe-option-desc">
                For active pups who make awesome walking, jogging, or hiking
                companions.
              </p>
            </div>

            {/* Option 2: Companionship */}
            <div className="edpe-option-wrapper">
              <div
                className={`edpe-option-card ${
                  formData.dogActivity === "companionship" ? "selected" : ""
                }`}
                onClick={() => updateField("dogActivity", "companionship")}
              >
                <div className="edpe-card-content">
                  <h3>COMPANIONSHIP</h3>
                </div>
                <div className="edpe-companion-img">
                  <img
                    src={option2Icon}
                    alt="Girl Walking"
                    className="edpe-img-contain"
                  />
                </div>
              </div>
              <p className="edpe-option-desc">
                For calmer pups who make perfect furry co-workers, reading
                buddies, or movie sidekicks.
              </p>
            </div>

            {/* Option 3: Both */}
            <div className="edpe-option-wrapper">
              <div
                className={`edpe-option-card edpe-full-width ${
                  formData.dogActivity === "both" ? "selected-blue" : ""
                }`}
                onClick={() => updateField("dogActivity", "both")}
              >
                <div className="edpe-btn-content">
                  {formData.dogActivity === "both" && (
                    <Check
                      size={24}
                      strokeWidth={3}
                      color="white"
                      className="edpe-desktop-check"
                    />
                  )}
                  {/* Mobile Check Override */}
                  {formData.dogActivity === "both" && (
                    <Check
                      size={20}
                      strokeWidth={3}
                      color="white"
                      className="edpe-mobile-check"
                    />
                  )}
                  <span
                    className={`edpe-btn-text ${
                      formData.dogActivity === "both" ? "white-text" : ""
                    }`}
                  >
                    BOTH
                  </span>
                </div>
              </div>
              <p className="edpe-option-desc">
                Best of both worlds: playful pups who love to move, and enjoy
                winding down for snuggles.
              </p>
            </div>
          </div>

          <MobileBottomBar
            onNext={() => isExperienceSelected && setMobileStep(2)}
            isNextEnabled={isExperienceSelected}
            nextIconSrc={nextIcon2}
            showSkip={true}
            skipText="EXIT"
            isSkipBlue={true}
            onSkip={() => navigate("/user-profile")}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL: Personality Inputs (Mobile Step 2) */}
      <div
        className={`edpe-panel edpe-right-panel ${mobileStep === 2 ? "edpe-mobile-active" : "edpe-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => setMobileStep(1)}
          backColor="#fff"
          logoColor="#fff"
        />

        <div className="edpe-content-wrapper edpe-prompts-wrapper">
          {/* Mobile Progress Bar (Mirrored) */}
          <div className="edpe-header-section edpe-mobile-only">
            <div className="edpe-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress Step"
                className="edpe-progress-icon"
                style={{ height: "60px", marginLeft: "-1rem" }}
              />
            </div>
            <div className="edpe-progress-track">
              <div className="edpe-progress-fill edpe-step-2-fill"></div>
            </div>
          </div>

          <h2 className="edpe-right-title">
            {formData.dogName
              ? "" + formData.dogName.toUpperCase() + "'S"
              : "DOG NAME'S"}{" "}
            PERSONALITY
          </h2>
          <p className="edpe-subtitle">
            Pick a prompt - Show off your pup's personality
          </p>

          <div className="edpe-prompts-list">
            {dynamicPrompts.map((prompt, index) => {
              const isSelected = formData.selectedPromptIndex === index;
              const rawVal = formData.rawAnswers?.[index] || "";

              return (
                <div
                  key={index}
                  className={`edpe-prompt-item ${isSelected ? "active" : ""}`}
                  onClick={() => updateField("selectedPromptIndex", index)}
                >
                  <label className="edpe-prompt-label">
                    {prompt} {!isSelected && !rawVal ? "...." : ""}
                  </label>
                  {isSelected ? (
                    <input
                      type="text"
                      className="edpe-prompt-input"
                      placeholder="Enter here"
                      autoFocus
                      value={rawVal}
                      onChange={(e) =>
                        handlePromptAnswerChange(index, prompt, e.target.value)
                      }
                    />
                  ) : rawVal ? (
                    <div className="edpe-saved-answer">{rawVal}</div>
                  ) : (
                    <div className="edpe-underline"></div>
                  )}
                </div>
              );
            })}

            {/* Custom Prompt Section */}
            {formData.selectedPromptIndex === "custom" ? (
              <div className="edpe-custom-prompt-container">
                <div className="edpe-custom-group">
                  <label>Your Question:</label>
                  <input
                    type="text"
                    className="edpe-prompt-input"
                    placeholder="e.g. Favorite toy..."
                    value={formData.customPromptQuestion || ""}
                    onChange={(e) =>
                      handleCustomChange(
                        e.target.value,
                        formData.customPromptAnswer || "",
                      )
                    }
                  />
                </div>
                <div className="edpe-custom-group">
                  <label>Your Answer:</label>
                  <input
                    type="text"
                    className="edpe-prompt-input"
                    placeholder="Enter answer here"
                    value={formData.customPromptAnswer || ""}
                    onChange={(e) =>
                      handleCustomChange(
                        formData.customPromptQuestion || "",
                        e.target.value,
                      )
                    }
                  />
                </div>
              </div>
            ) : (
              <div
                className="edpe-write-own-btn"
                onClick={() => updateField("selectedPromptIndex", "custom")}
              >
                Write your own
              </div>
            )}
          </div>

          {/* Footer Navigation */}
          <div className="edpe-footer-nav edpe-step-2-next desktop-only">
            <button
              className="edpe-skip-btn"
              onClick={() => navigate("/edit-team-paws-personality")}
            >
              SKIP
            </button>

            <button
              type="button"
              className="edpe-skip-btn"
              onClick={() => navigate("/user-profile")}
            >
              EXIT
            </button>

            <button
              className={`edpe-next-btn-group ${isNextEnabled && !isUpdating ? "enabled" : "disabled"}`}
              onClick={isNextEnabled ? handleNext : undefined}
              disabled={isUpdating}
            >
              <span className="edpe-next-text">
                {isUpdating ? "UPDATING..." : "NEXT"}
              </span>
              <div className="edpe-next-circle">
                {isUpdating ? (
                  <Loader2 className="animate-spin" color="white" />
                ) : (
                  <img src={nextIcon} alt="Next" className="edpe-next-img" />
                )}
              </div>
            </button>
          </div>
          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isNextEnabled && !isUpdating}
            nextIconSrc={nextIcon}
            showSkip={true}
            skipText="EXIT"
            onSkip={() => navigate("/user-profile")}
            variant="green"
          />
        </div>
      </div>
    </div>
  );
};

export default DogPersonalityEditPage;
